import { Controller, useForm } from 'react-hook-form';
import { VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { OnboardingCompanyProfile } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import zodLocales from 'common/zod/locales';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import Header from '../../../../components/Header';
import locales from './locales';

const validationSchema = z.object({
  hasClientUnderSanctions: z.boolean({
    invalid_type_error: zodLocales.errors.required,
    required_error: zodLocales.errors.required,
  }),
});

type FormResult = z.infer<typeof validationSchema>;

type FormInput = {
  hasClientUnderSanctions: OnboardingCompanyProfile['hasClientUnderSanctions'];
};

interface CompanyMemberSanctionsProps {
  // Not used for now but needed to adapt locales after
  isMandatee: boolean;
  isSaving: boolean;
  onSave: (values: FormResult) => void;
  companyProfile: Pick<
    OnboardingCompanyProfile,
    'legalName' | 'hasClientUnderSanctions'
  >;
}

const getTitle = (legalName: string): string => {
  return legalName
    ? (locales.formatString(locales.title, {
        legalName,
      }) as string)
    : locales.titleNoLegalName;
};

const getDescription = (isMandatee: boolean): string => {
  if (isMandatee) {
    return locales.descriptionMandatee;
  }

  return locales.description;
};

const CompanyMemberSanctions = asBentoPage<CompanyMemberSanctionsProps>(
  ({ companyProfile, isMandatee, isSaving, Layout, onPrev, onSave }) => {
    const {
      control,
      formState: { errors },
      handleSubmit,
    } = useForm<FormInput>({
      defaultValues: {
        hasClientUnderSanctions: companyProfile.hasClientUnderSanctions,
      },
      resolver: zodResolver(validationSchema),
    });

    const onSubmit = handleSubmit((values) => {
      onSave(values as FormResult);
    });

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Header
          imageHeightPx={80}
          imageWidthPx={120}
          title={getTitle(companyProfile.legalName)}
        />
        <form onSubmit={onSubmit}>
          <VStack
            alignItems="flex-start"
            as="form"
            onSubmit={onSubmit}
            spacing="space-16"
          >
            <Form.Field
              error={errors.hasClientUnderSanctions?.message}
              label={getDescription(isMandatee)}
            >
              <Controller
                control={control}
                name="hasClientUnderSanctions"
                render={({ field: { onChange, value } }) => (
                  <Form.Radio.Group
                    direction="row"
                    isDisabled={isSaving}
                    onChange={(val) => onChange(val === 'true')}
                    radioPosition="right"
                    value={value === undefined ? undefined : String(value)}
                    variant="outline"
                  >
                    <Form.Radio value="true">
                      <Form.Radio.Title>{locales.yes}</Form.Radio.Title>
                    </Form.Radio>

                    <Form.Radio value="false">
                      <Form.Radio.Title>{locales.no}</Form.Radio.Title>
                    </Form.Radio>
                  </Form.Radio.Group>
                )}
              />
            </Form.Field>
          </VStack>
          <Button
            isExpanded
            isLoading={isSaving}
            marginTop="space-40"
            type="submit"
          >
            {locales.nextButtonLabel}
          </Button>
        </form>
      </Layout>
    );
  },
);

export default CompanyMemberSanctions;
